import { forwardRef, useRef } from 'react';
import TinderCard from 'react-tinder-card';
import styled from 'styled-components';
import { resolveMention } from '../../store/tasks/tasksSlice';
import { appDispatch } from '../../store/store';
import { useTranslation } from 'react-i18next';
import { orange } from '../../utils/style/colors';
import Icon from '../Icon/Icon';

const TinderContainer = styled.div`
  position: absolute;
  padding: 0.5rem;
  display: contents;
  justify-content: center;
`;

const Content = styled.div<{ hasShadow: boolean }>`
  position: relative;
  background-color: var(--exact-white);
  width: 100%;
  max-width: 100%;
  height: 16rem;
  background-size: cover;
  border-radius: 2rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  box-shadow: ${(props) =>
    props.hasShadow ? '1px 1px 6px lightgrey' : 'none'};
  overflow: hidden;

  @media screen and (max-width: 900px) {
    height: 20rem;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  border-radius: 2rem;
  transition: all 0.5s ease-in-out;
  color: var(--exact-white);
  font-size: 2rem;
  text-align: ${(props) => (props.cardDir === 'right' ? 'left' : 'right')};
  padding: 1rem 1rem 0;
  pointer-events: none;
  display: flex;
  gap: 0.5rem;

  flex-direction: ${(props) =>
    props.cardDir === 'right' ? 'row-reverse' : 'row'};
`;

interface ISwipCardProps {
  item: { id: string | number; [key: string]: any };
  children: React.ReactNode;
  isCurrentItemIsOnTop: boolean;
  ref?: any;
  handleBeforeSwipe: (taskId: number) => void;
  handleAfterSwipe: () => void;
}

const SwipCard = forwardRef<any, ISwipCardProps>(
  (
    {
      item,
      children,
      isCurrentItemIsOnTop,
      handleBeforeSwipe,
      handleAfterSwipe,
    },
    ref
  ) => {
    const { t } = useTranslation('general');

    const remindMeLaterRef = useRef<any>();
    const resolveRef = useRef<any>();

    const swiped = (direction: string, _: string) => {
      handleBeforeSwipe(Number(item.id));
      if (direction === 'left') {
        appDispatch(
          resolveMention({ mentionId: undefined, taskId: Number(item.id) })
        );
      }
      handleAfterSwipe();
      resetStyle();
    };

    const handleFulfilledSwipe = (
      dir: 'left' | 'right' | 'up' | 'down' | 'none'
    ) => {
      resolveRef.current!.style.opacity = dir === 'left' ? 0.5 : '0';
      remindMeLaterRef.current!.style.opacity = dir === 'right' ? 0.5 : '0';
    };

    const resetStyle = () => {
      handleFulfilledSwipe('none');
    };
    const handleUnfulfilledSwipe = () => {
      handleFulfilledSwipe('none');
    };

    return (
      <TinderContainer>
        <TinderCard
          ref={ref}
          className="absolute w-full"
          key={item.name}
          onSwipe={(dir) => swiped(dir, item.name)}
          onSwipeRequirementFulfilled={handleFulfilledSwipe}
          onSwipeRequirementUnfulfilled={handleUnfulfilledSwipe}
          preventSwipe={['up', 'down']}
          swipeRequirementType={'position'}
          swipeThreshold={30}
          flickOnSwipe={true}
        >
          <div className="relative">
            <Content hasShadow={isCurrentItemIsOnTop}>{children}</Content>
            <Overlay
              ref={remindMeLaterRef}
              style={{ background: orange }}
              cardDir="right"
            >
              <div>{t('remindMeLater')}</div>
              <div>
                <Icon type={'clock'} color={'var(--exact-white)'} />
              </div>
            </Overlay>
            <Overlay
              ref={resolveRef}
              style={{ background: 'green' }}
              cardDir="left"
            >
              <div>
                <Icon type={'check-circle'} color={'var(--exact-white)'} />
              </div>
              <div>{t('seen')}</div>
            </Overlay>
          </div>
        </TinderCard>
      </TinderContainer>
    );
  }
);

export default SwipCard;
